import type { ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/cn";

export interface TooltipProps {
  /** Anchor element — must forward a ref (Radix requires a real DOM node). */
  children: ReactNode;
  /** Body content rendered in the floating panel. Strings render plain; JSX is supported. */
  content: ReactNode;
  /**
   * Per-root override for the open delay. When omitted, the delay falls back
   * to whatever the ancestor `<TooltipProvider>` was mounted with (200ms in
   * `main.tsx`). Pass a value here only when a specific call site needs a
   * different delay than the global default.
   */
  delayDuration?: number;
  /** Preferred side of the anchor to render on. Radix handles collision fallbacks. */
  side?: TooltipPrimitive.TooltipContentProps["side"];
  /** Padding (px) between the anchor and the tooltip panel. */
  sideOffset?: number;
  /** When `false`, suppresses rendering — handy for conditional tooltips. */
  enabled?: boolean;
  className?: string;
}

/**
 * App-styled Radix tooltip wrapper. Callers drop `<Tooltip content="…">` around
 * any element without repeating the Radix boilerplate, and the panel picks up
 * the app's surface/border/elevation tokens so the chrome matches every other
 * popover surface.
 *
 * IMPORTANT: A single `<TooltipProvider>` MUST be mounted globally (see
 * `main.tsx`, `delayDuration={200}`). This primitive intentionally does NOT
 * wrap itself in a provider — doing so would reset `delayDuration` to Radix's
 * 700ms internal default at every call site (because an unspecified
 * `delayDuration` prop on a nested `Provider` is not the same as "inherit").
 *
 * For unit tests or isolated renders (Storybook, ad-hoc previews), wrap the
 * subject tree with `<TooltipProvider delayDuration={0}>` in your test utility.
 */
export function Tooltip({
  children,
  content,
  delayDuration,
  side = "top",
  sideOffset = 6,
  enabled = true,
  className,
}: TooltipProps) {
  if (!enabled) {
    return <>{children}</>;
  }
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={sideOffset}
          className={cn(
            "z-50 max-w-xs rounded-[var(--r-sm)] bg-[var(--ink)] px-2 py-1",
            "text-xs text-[var(--bg)] shadow-[var(--shadow-2)]",
            "data-[state=delayed-open]:animate-overlay-fade-in",
            className,
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/**
 * Re-export the Radix provider so `main.tsx` can mount it once at the root,
 * and so test utilities can wrap isolated renders without importing directly
 * from `@radix-ui/react-tooltip`.
 */
export const TooltipProvider = TooltipPrimitive.Provider;
